import React, { useEffect, useState } from "react";
import api, { API, formatApiErrorDetail, getAuthToken } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, FileText } from "lucide-react";

const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function ClientQuotations() {
    const { hasPerm } = useAuth();
    const [items, setItems] = useState([]);
    const [clients, setClients] = useState([]);
    const [show, setShow] = useState(false);
    const [form, setForm] = useState({ client_id: "", title: "", notes: "", lines: [] });
    const [err, setErr] = useState("");

    const load = async () => {
        const [q, c] = await Promise.all([api.get("/client-quotations"), api.get("/clients")]);
        setItems(q.data); setClients(c.data);
    };
    useEffect(() => { load(); }, []);

    const addLine = () => setForm({ ...form, lines: [...form.lines, { part_code: "", description: "", hsn_code: "", qty: 1, rate: 0, gst_rate: 18 }] });
    const setLine = (i, p) => { const a = [...form.lines]; a[i] = { ...a[i], ...p }; setForm({ ...form, lines: a }); };
    const rm = (i) => setForm({ ...form, lines: form.lines.filter((_, j) => j !== i) });

    const submit = async (e) => {
        e.preventDefault(); setErr("");
        try { await api.post("/client-quotations", form); setShow(false); setForm({ client_id: "", title: "", notes: "", lines: [] }); load(); }
        catch (err) { setErr(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    };
    const dl = (cq) => {
        const tok = getAuthToken();
        fetch(`${API}/client-quotations/${cq.id}/pdf`, { credentials: "include", headers: tok ? { Authorization: `Bearer ${tok}` } : {} })
            .then(r => r.blob()).then(b => { const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = `${cq.quote_number}.pdf`; a.click(); });
    };

    return (
        <div data-testid="cq-page" className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1">Sales</div>
                    <h1 className="text-3xl font-heading font-semibold text-stone-900">Client quotations</h1>
                </div>
                {hasPerm("clientquotation.create") && <button data-testid="new-cq-btn" onClick={() => setShow(true)} className="bg-amber-500 hover:bg-amber-600 text-white rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2"><Plus size={15} /> New quotation</button>}
            </div>
            <div className="bg-white border border-stone-200 rounded-xl overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
                            <th className="text-left py-3 px-4">Number</th>
                            <th className="text-left py-3 px-4">Title</th>
                            <th className="text-left py-3 px-4">Client</th>
                            <th className="text-right py-3 px-4">Total</th>
                            <th className="text-right py-3 px-4"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(cq => (
                            <tr key={cq.id} className="border-t border-stone-100 hover:bg-stone-50">
                                <td className="py-3 px-4 font-mono text-xs">{cq.quote_number}</td>
                                <td className="py-3 px-4">{cq.title}</td>
                                <td className="py-3 px-4">{cq.client_name}</td>
                                <td className="py-3 px-4 text-right font-medium">{inr(cq.total)}</td>
                                <td className="py-3 px-4 text-right"><button onClick={() => dl(cq)} className="text-xs text-amber-700 inline-flex items-center gap-1"><FileText size={12} /> PDF</button></td>
                            </tr>
                        ))}
                        {items.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-stone-500 text-sm">No client quotations yet.</td></tr>}
                    </tbody>
                </table>
            </div>

            {show && (
                <div className="fixed inset-0 z-40 bg-stone-900/40 flex items-center justify-center p-4">
                    <div className="bg-white border border-stone-200 rounded-xl max-w-4xl w-full max-h-[92vh] overflow-y-auto">
                        <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between sticky top-0 bg-white">
                            <div className="font-heading font-semibold text-stone-900">New client quotation</div>
                            <button onClick={() => setShow(false)} className="text-stone-500"><X size={18} /></button>
                        </div>
                        <form onSubmit={submit} className="p-6 space-y-4">
                            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
                            <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                    <label className="overline block mb-1">Client *</label>
                                    <select required value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3">
                                        <option value="">Select client</option>
                                        {clients.map(c => <option key={c.id} value={c.id}>{c.company || c.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="overline block mb-1">Title *</label>
                                    <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3" />
                                </div>
                            </div>
                            <div className="border border-stone-200 rounded-xl overflow-x-auto">
                                <table className="w-full text-sm min-w-[800px]">
                                    <thead className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
                                        <tr><th className="text-left py-2 px-3">Part</th><th className="text-left py-2 px-3">Description</th><th className="text-left py-2 px-3">HSN</th><th className="text-right py-2 px-3">Qty</th><th className="text-right py-2 px-3">Rate</th><th className="text-right py-2 px-3">GST %</th><th></th></tr>
                                    </thead>
                                    <tbody>
                                        {form.lines.map((l, i) => (
                                            <tr key={i} className="border-t border-stone-100">
                                                <td className="p-1"><input value={l.part_code} onChange={(e) => setLine(i, { part_code: e.target.value })} className="w-28 border border-stone-200 rounded-md text-sm py-1.5 px-2 font-mono" /></td>
                                                <td className="p-1"><input value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} className="w-full border border-stone-200 rounded-md text-sm py-1.5 px-2" /></td>
                                                <td className="p-1"><input value={l.hsn_code} onChange={(e) => setLine(i, { hsn_code: e.target.value })} className="w-24 border border-stone-200 rounded-md text-sm py-1.5 px-2 font-mono" /></td>
                                                <td className="p-1"><input type="number" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} className="w-20 border border-stone-200 rounded-md text-sm py-1.5 px-2 text-right" /></td>
                                                <td className="p-1"><input type="number" value={l.rate} onChange={(e) => setLine(i, { rate: e.target.value })} className="w-24 border border-stone-200 rounded-md text-sm py-1.5 px-2 text-right" /></td>
                                                <td className="p-1"><input type="number" value={l.gst_rate} onChange={(e) => setLine(i, { gst_rate: e.target.value })} className="w-16 border border-stone-200 rounded-md text-sm py-1.5 px-2 text-right" /></td>
                                                <td className="p-1"><button type="button" onClick={() => rm(i)} className="text-stone-400 hover:text-red-700">×</button></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <button type="button" onClick={addLine} className="text-sm text-amber-700 inline-flex items-center gap-1"><Plus size={13} /> Add line</button>
                            <div className="flex justify-end gap-3 pt-2">
                                <button type="button" onClick={() => setShow(false)} className="text-sm text-stone-600 px-4 py-2 rounded-md">Cancel</button>
                                <button type="submit" className="bg-amber-500 hover:bg-amber-600 text-white rounded-md px-4 py-2 text-sm font-medium">Create</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
